import {
  FormControlLabel,
  Grid,
  Paper,
  Switch,
  Typography,
} from '@material-ui/core';
import { DelayedLinearProgress, ResponsiveIconButton } from 'components';
import { useLocalStorageSettings } from 'hooks';
import { PendingItem } from 'interfaces';
import * as React from 'react';
import { OrderService } from 'services';
import { collapseCombos } from '../../collapseCombos';
import { FirebaseService } from 'services/FirebaseService';
import { LidsOrderCard } from './components';
import RefreshIcon from '@material-ui/icons/Refresh';

export const LidsView: React.FC = () => {
  const [loading, setLoading] = React.useState(false);
  const [starredOnly, setStarredOnly] = React.useState(false);
  const [items, setItems] = React.useState<
    { orderId: number; starred: boolean; items: PendingItem[] }[]
  >([]);
  const [savedStarred, setSavedStarred] = useLocalStorageSettings<number[]>(
    'starred_orders_lids',
    [],
  );

  const loadOrderItems = React.useCallback(
    function loadOrderItems() {
      setLoading(true);
      Promise.all([OrderService.getOrderItemsForPrepartion()])
        .then(([orderResponse]) => {
          // Drop orders whose Lids checklist has been finalised by the
          // user (Complete button → orders.lids_completed_at set).
          // The Packing flow's order_status_id is left alone.
          const visibleResponse = orderResponse.filter(
            (it) => it.lidsCompletedAt === null,
          );

          // Same grouping pattern as the Packing tab: group by orderId
          // and split items with quantity > 1 into N cards-of-one so
          // each lid can be ticked independently.
          const orders = visibleResponse.reduce<
            { orderId: number; starred: boolean; items: PendingItem[] }[]
          >((prev, curr) => {
            let orderIndex = prev.findIndex((p) => p.orderId === curr.orderId);

            const splitItems =
              curr.quantity > 1
                ? Array.from(Array(curr.quantity).keys()).map((c) => ({
                    ...curr,
                    quantity: 1,
                  }))
                : [curr];

            if (orderIndex === -1) {
              return [
                ...prev,
                {
                  orderId: curr.orderId,
                  starred: savedStarred.includes(curr.orderId),
                  items: [...splitItems],
                },
              ];
            } else {
              prev[orderIndex].items.push(...splitItems);
            }

            return prev;
          }, []);

          // Collapse combos into one line, exactly like the Packing tab
          // (shared helper). The collapsed line carries every component's
          // menuItemId so ticking it still records a lid per component.
          const collapsed = orders.map((order) => ({
            ...order,
            items: collapseCombos(order.items),
          }));

          setItems(collapsed);
        })
        .finally(() => setLoading(false));
    },
    [savedStarred],
  );

  React.useEffect(() => {
    loadOrderItems();
  }, [loadOrderItems]);

  // Live refresh: re-fetch when Firebase signals an /orders mutation.
  // Debounced so a burst of events (e.g. one order's items written in
  // sequence) results in a single API roundtrip. The manual Refresh
  // button stays as a fallback if Firebase drifts.
  React.useEffect(() => {
    const ref = FirebaseService.getDatabase().ref('orders');
    let timer: ReturnType<typeof setTimeout> | null = null;
    const handler = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        loadOrderItems();
      }, 1500);
    };
    ref.on('value', handler);
    return () => {
      ref.off('value', handler);
      if (timer) clearTimeout(timer);
    };
  }, [loadOrderItems]);

  return (
    <React.Fragment>
      <Paper
        style={{
          padding: 16,
          marginBottom: 16,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <ResponsiveIconButton
          onClick={(e) => {
            loadOrderItems();
          }}
          color="primary"
          icon={RefreshIcon}
        >
          Refresh Orders
        </ResponsiveIconButton>

        <Typography variant="subtitle2" color="inherit">
          Number of orders needing lids: {items.length}
        </Typography>
        <FormControlLabel
          control={
            <Switch
              value="starredOnly"
              checked={starredOnly}
              onChange={(evt, checked) => setStarredOnly(checked)}
            />
          }
          label="Show Starred Only"
        />
      </Paper>

      {loading && <DelayedLinearProgress />}
      <Grid container>
        {items
          .filter((i) => !starredOnly || i.starred === starredOnly)
          .map((order, index) => (
            <LidsOrderCard
              key={order.orderId}
              order={order}
              onCompleteOrder={(orderId: number) => {
                OrderService.lidsCompleteOrder(orderId).then(() => {
                  let newVal = items.slice();
                  newVal = newVal.filter((v) => v.orderId !== orderId);
                  setItems(newVal);
                });
              }}
              toggleStarred={(orderId: number) => {
                const newVal = items.slice();
                const orderIndex = newVal.findIndex(
                  (v) => v.orderId === orderId,
                );

                if (orderIndex !== -1) {
                  newVal[orderIndex].starred = !newVal[orderIndex].starred;
                }

                setItems(newVal);

                setSavedStarred(
                  newVal.filter((v) => v.starred).map((v) => v.orderId),
                );
              }}
            />
          ))}
      </Grid>
    </React.Fragment>
  );
};
